import { useEffect, type MouseEvent, type ReactNode } from 'react';
import { useLocation, useMatch } from 'react-router';
import { api } from '../lib/api.js';
import { useSession } from '../state/session.js';

export function useEmbed(): boolean {
  const { search } = useLocation();
  const thread = useMatch('/p/:projectId/c/:conversationId');
  return Boolean(thread && new URLSearchParams(search).get('embed') === '1');
}

/** Referrer selects an allowed parent, never grants permission to send a status. */
export function embedParentOrigin(referrer: string, allowed: string[]): string | null {
  try {
    const origin = new URL(referrer).origin;
    return allowed.includes(origin) ? origin : null;
  } catch { return null; }
}

export function EmbedStatus() {
  const embed = useEmbed();
  const { user, loading } = useSession();
  useEffect(() => {
    if (!embed || loading || window.parent === window) return;
    let alive = true;
    void api.authConfig().then((config) => {
      const origin = embedParentOrigin(document.referrer, config.embedParentOrigins ?? []);
      if (alive && origin) window.parent.postMessage({ type: 'jabberr:embed', signedIn: Boolean(user) }, origin);
    }).catch(() => undefined);
    return () => { alive = false; };
  }, [embed, loading, user]);
  return null;
}

export function EmbedSignIn() {
  return <div className="page embed-sign-in"><p>Sign in to Jabberr, then reload this panel.</p>
    <a className="btn btn-primary" href="/login" target="_blank" rel="noopener">Sign in to Jabberr ↗</a>
  </div>;
}

export function EmbedThreadShell({ children }: { children: ReactNode }) {
  // Stop React Router's link handler; let the browser open every link in a separate tab.
  const openLink = (event: MouseEvent<HTMLDivElement>) => {
    const anchor = (event.target as Element).closest('a');
    if (!anchor) return;
    anchor.target = '_blank';
    anchor.rel = 'noopener';
    event.stopPropagation();
  };
  return <div className="embed-thread" onClickCapture={openLink} onAuxClickCapture={openLink}>{children}</div>;
}
